import * as React from "react";
import { StudyCoughSetting, StudyPopulationGroup, DeviceType, StudyFvcSetting, SessionType, Language, StudySvcSetting, StudyPcfSetting, StudyEcgSetting, StudyDlcoSetting, StudyDiarySetting, StudyPressureSetting, StudySubjectSetting, TableParameters, StudyPulseOximetrySetting, StudyPefSetting } from "../misc/api/apiTypes";
import { ParameterEntity } from "../misc/api/SerenityReferenceData";
import { DiaryConfigurationFile, StudyCountry, DataItemConfiguration, Study } from "webportalclient";
import LoadingWithMessage from "../misc/components/LoadingWithMessage";
import { DefaultPaging } from "../Components/Query";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../misc/api/WebPortalTokenClient";

interface CurrentStudyConfigurationContextState {
    study: Study;
    dataItemConfigurationCollection: DataItemConfiguration[];
    populationGroups: StudyPopulationGroup[];
    deviceTypes: DeviceType[];
    sessionTypes: SessionType[];
    parameters: ParameterEntity[];
    fvcSettings: StudyFvcSetting;
    svcSettings: StudySvcSetting;
    pcfSettings: StudyPcfSetting;
    coughSettings: StudyCoughSetting;
    ecgSettings: StudyEcgSetting;
    dlcoSettings: StudyDlcoSetting;
    diarySettings: StudyDiarySetting;
    mipSettings: StudyPressureSetting;
    mepSettings: StudyPressureSetting;
    snipSettings: StudyPressureSetting;
    pulseOximetrySettings: StudyPulseOximetrySetting;
    pefSettings: StudyPefSetting;
    languages: Language[];
    diaryConfigFiles: DiaryConfigurationFile[];
    countries: StudyCountry[];
    studySubjectSetting: StudySubjectSetting;
    tableParameters: TableParameters;
    refreshSettings: () => void;
}

export const CurrentStudyConfigurationContext = React.createContext<CurrentStudyConfigurationContextState | undefined>(undefined);

interface CurrentStudyConfigurationContextProviderProps {
    currentStudy: Study;
    children: React.ReactNode;
}

export const CurrentStudyConfigurationContextProvider = ({ currentStudy, children }: CurrentStudyConfigurationContextProviderProps) => {
    const [dataItems, setDataItems] = React.useState<DataItemConfiguration[]>([]);
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();
    const [populationGroups, setPopulationGroups] = React.useState([]);
    const [deviceTypes, setDeviceTypes] = React.useState([]);
    const [sessionTypes, setSessionTypes] = React.useState([]);
    const [fvcSettings, setFvcSettings] = React.useState<StudyFvcSetting>();
    const [svcSettings, setSvcSettings] = React.useState<StudySvcSetting>();
    const [pulseOximetrySettings, setPulseOximetrySettings] = React.useState<StudyPulseOximetrySetting>();
    const [pcfSettings, setPcfSettings] = React.useState<StudyPcfSetting>();
    const [coughSettings, setCoughSettings] = React.useState<StudyCoughSetting>();
    const [ecgSettings, setEcgSettings] = React.useState<StudyEcgSetting>();
    const [dlcoSettings, setDlcoSettings] = React.useState<StudyDlcoSetting>();
    const [diarySettings, setDiarySettings] = React.useState<StudyDiarySetting>();
    const [mipSettings, setMipSettings] = React.useState<StudyPressureSetting>();
    const [mepSettings, setMepSettings] = React.useState<StudyPressureSetting>();
    const [snipSettings, setSnipSettings] = React.useState<StudyPressureSetting>();
    const [pefSettings, setPefSettings] = React.useState<StudyPefSetting>();
    const [parameters, setParameters] = React.useState<ParameterEntity[]>([]);
    const [languages, setLanguages] = React.useState<Language[]>([]);
    const [countries, setCountries] = React.useState<StudyCountry[]>([]);
    const [diaryConfigFiles, setDiaryConfigFiles] = React.useState<DiaryConfigurationFile[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [refreshStudySettings, setRefreshStudySettings] = React.useState(false);
    const [studySubjectSetting, setStudySubjectSetting] = React.useState<StudySubjectSetting>();
    const [tableParameters, setTableParameters] = React.useState<TableParameters>({DefaultPageSize: 10});

    const getStudySettings = async () => {
        const fvcSettingsResult = await legacyClient.getStudyFvcSettings();
        setFvcSettings(fvcSettingsResult);

        const svcSettingsResult = await legacyClient.getStudySvcSettings();
        setSvcSettings(svcSettingsResult);

        const pulseOximetrySettings = await legacyClient.getStudyPulseOximetrySettings();
        setPulseOximetrySettings(pulseOximetrySettings);

        const pcfSettingsResult = await legacyClient.getStudyPcfSettings();
        setPcfSettings(pcfSettingsResult);

        const ecgSettingsResult = await legacyClient.getStudyEcgSettings();
        setEcgSettings(ecgSettingsResult);

        const dlcoSettingsResult = await legacyClient.getStudyDlcoSettings();
        setDlcoSettings(dlcoSettingsResult);

        const diarySettingsResult = await legacyClient.getStudyDiarySettings(currentStudy.id);
        setDiarySettings(diarySettingsResult);

        const mipSettingsResult = await legacyClient.getStudyMipSettings(currentStudy.id);
        setMipSettings(mipSettingsResult);

        const mepSettingsResult = await legacyClient.getStudyMepSettings(currentStudy.id);
        setMepSettings(mepSettingsResult);

        const snipSettingsResult = await legacyClient.getStudySnipSettings(currentStudy.id);
        setSnipSettings(snipSettingsResult);

        const subjectSettings = await legacyClient.getStudySubjectSettings();
        setStudySubjectSetting(subjectSettings);

        const pefSettingsResult = await legacyClient.getStudyPefSettings(currentStudy.id);
        setPefSettings(pefSettingsResult);

        setRefreshStudySettings(false);
    }

    React.useEffect(() => {
        const getDataItemConfigurations = async () => {
            const defaultDataItems = await client.client.getConfigurableDataItems();
            const dataItemsResult = await client.client.getDataItemConfigurations(undefined, `studyId eq ${currentStudy.id}`);

            const defaults = defaultDataItems.value.map(ci => {
                const customItem = dataItemsResult.value.find(di => di.configurableDataItemId === ci.id);

                return {
                    displayText: customItem?.displayText ?? ci.defaultDisplayText,
                    configurableDataItemId: ci.id,
                    formatId: customItem?.formatId ?? ci.defaultFormatId,
                    customFormat: customItem?.customFormat ?? ci.customFormat,
                    minimumLength: customItem?.minimumLength ?? ci.defaultMinimumLength,
                    maximumLength: customItem?.maximumLength ?? ci.defaultMaximumLength,
                    minimumValue: customItem?.minimumValue ?? ci.defaultMinimumValue,
                    maximumValue: customItem?.maximumValue ?? ci.defaultMaximumValue,
                    unitTypeId: customItem?.unitTypeId ?? ci.defaultUnitTypeId,
                    importanceId: customItem?.importanceId ?? ci.defaultImportanceId,
                    prefixWithSiteNumber: customItem?.prefixWithSiteNumber ?? ci.defaultPrefixWithSiteNumber,
                    prefixDelimiter: customItem?.prefixDelimiter ?? "",
                    created: customItem?.created ?? null,
                    modified: customItem?.modified ?? null
                }
            });

            setDataItems(defaults);
        }

        const getData = async () => {
            setLoading(true);

            getDataItemConfigurations();

            const populationGroupsResult = await legacyClient.getStudyPopulationGroupByStudy(currentStudy.id);
            setPopulationGroups(populationGroupsResult);

            const deviceTypesResult = await legacyClient.getStudyDeviceTypes(currentStudy.id);
            setDeviceTypes(deviceTypesResult);

            const sessionTypesResult = await legacyClient.getStudySessionTypes(currentStudy.id);
            setSessionTypes(sessionTypesResult);

            const studyCountries = await client.client.getStudyCountries(undefined, `studyId eq ${currentStudy.id}`);
            setCountries(studyCountries.value);

            const parametersResult = await legacyClient.getParameters();
            setParameters(parametersResult);

            const languagesResult = await legacyClient.getLanguages(currentStudy.id);
            setLanguages(languagesResult);

            const tableParameters = await legacyClient.getTableParameters();
            setTableParameters(tableParameters);
            DefaultPaging.size = tableParameters.DefaultPageSize;

            const studyCoughSettings = await legacyClient.getStudyCoughSettings();
            setCoughSettings(studyCoughSettings);

            getStudySettings();

            const getDiaryLanguageStringsFile = async (studyId) => {
                const result = await client.client.getDiaryConfigurations(undefined, `studies/any(props: props/id eq ${studyId})`, undefined, "studies,files");

                return result.value && result.value[0] && result.value[0].files;
            }

            const diaryConfigFilesResult = await getDiaryLanguageStringsFile(currentStudy.id);
            setDiaryConfigFiles(diaryConfigFilesResult ?? []);

            setLoading(false);
        }

        getData();
    }, [currentStudy]);

    React.useEffect(() => {
        if (refreshStudySettings) {
            getStudySettings();
        }
    }, [refreshStudySettings]);

    const refreshSettings = () => {
        setRefreshStudySettings(true);
    };

    

    if (loading)
        return <LoadingWithMessage message="Loading study..." />

    return (
        <CurrentStudyConfigurationContext.Provider value={{
            study: currentStudy,
            dataItemConfigurationCollection: dataItems,
            populationGroups: populationGroups,
            deviceTypes: deviceTypes,
            sessionTypes: sessionTypes,
            parameters: parameters,
            fvcSettings: fvcSettings,
            svcSettings: svcSettings,
            pcfSettings: pcfSettings,
            coughSettings: coughSettings,
            ecgSettings: ecgSettings,
            dlcoSettings: dlcoSettings,
            diarySettings: diarySettings,
            mipSettings: mipSettings,
            mepSettings: mepSettings,
            snipSettings: snipSettings,
            pulseOximetrySettings: pulseOximetrySettings,
            pefSettings: pefSettings,
            languages: languages,
            diaryConfigFiles: diaryConfigFiles,
            countries: countries,
            tableParameters: tableParameters,
            refreshSettings: refreshSettings,
            studySubjectSetting: studySubjectSetting,
        }}>
            {children}
        </CurrentStudyConfigurationContext.Provider>
    );
}

export const useCurrentStudyConfigurationContext = (): CurrentStudyConfigurationContextState => {
    const context = React.useContext(CurrentStudyConfigurationContext);
    if (context === undefined) {
        throw new Error('useCurrentStudyConfigurationContext must be used within a CurrentStudyConfigurationContext Provider')
    }

    return context;
}
